/*─────────────────────────────────────────────────────────────────────────────
 * ReportsRoutes.tsx
 *
 * Route group for the Reports section of the menu:
 *   - CDR (call detail records, filters, settings)
 *   - PBX Reports (SIP endpoints, active calls)
 *   - IVR (stats)
 *   - Call Center (queue callback report)
 *   - System Logs (audit logs, user activity, change history, security logs)
 *─────────────────────────────────────────────────────────────────────────────*/

import { Route } from 'react-router-dom';

import {
  AuditLogsProtectedRoute,
  CallDetailRecordsProtectedRoute,
  CdrFiltersProtectedRoute,
  CdrSettingsProtectedRoute,
  ChangeHistoryProtectedRoute,
  DashboardProtectedRoute,
  IvrsProtectedRoute,
  PbxSipEndpointsProtectedRoute,
  QueuesProtectedRoute,
  SecurityLogsProtectedRoute,
  UserActivityProtectedRoute
} from '../components/ProtectedRoute';

// CDR
import { CdrRecordsPage } from '../modules/callDetailRecords';
import { CdrFiltersPage, CdrFilterFormView } from '../modules/cdrFilters';
import { CdrSettingsPage } from '../modules/cdrSettings';

// PBX Reports
import { SipEndpointsPage } from '../modules/sipEndpoints';
import { ActiveCallsPage } from '../modules/activeCalls';

// IVR
import { IVRStatsPage } from '../modules/ivrStats';

// Call Center Reports
import { QueueCallbackReportPage } from '../modules/queueCallbackReport';

// System Logs
import AuditLogsPage from '../modules/auditLogs';
import UserActivityPage from '../modules/userActivity/UserActivityPage';
import ChangeHistoryPage from '../modules/changeHistory/ChangeHistoryPage';
import SecurityLogsPage from '../modules/securityLogs/SecurityLogsPage';

/**
 * ReportsRoutes
 *
 * Returns a JSX fragment containing all <Route> elements for the Reports section.
 * Consumed by App.tsx inside the authenticated <Routes> block.
 */
export const ReportsRoutes = () => (
  <>
    {/* ── CDR ─────────────────────────────────────────────────────────────── */}
    <Route path="/reports/cdr/cdr" element={<CallDetailRecordsProtectedRoute><CdrRecordsPage /></CallDetailRecordsProtectedRoute>} />
    <Route path="/reports/cdr/filters" element={<CdrFiltersProtectedRoute><CdrFiltersPage /></CdrFiltersProtectedRoute>} />
    <Route path="/reports/cdr/filters/new" element={<CdrFiltersProtectedRoute><CdrFilterFormView /></CdrFiltersProtectedRoute>} />
    <Route path="/reports/cdr/filters/:id" element={<CdrFiltersProtectedRoute><CdrFilterFormView /></CdrFiltersProtectedRoute>} />
    <Route path="/settings/pbx/cdr-settings" element={<CdrSettingsProtectedRoute><CdrSettingsPage /></CdrSettingsProtectedRoute>} />

    {/* ── PBX Reports ─────────────────────────────────────────────────────── */}
    <Route path="/reports/pbx/sip-endpoints" element={<PbxSipEndpointsProtectedRoute><SipEndpointsPage /></PbxSipEndpointsProtectedRoute>} />
    <Route path="/reports/pbx/active-calls" element={<DashboardProtectedRoute><ActiveCallsPage /></DashboardProtectedRoute>} />

    {/* ── IVR Reports ─────────────────────────────────────────────────────── */}
    <Route path="/reports/ivr/stats" element={<IvrsProtectedRoute><IVRStatsPage /></IvrsProtectedRoute>} />

    {/* ── Call Center Reports ──────────────────────────────────────────────── */}
    <Route path="/reports/call-center/queue-callback" element={<QueuesProtectedRoute><QueueCallbackReportPage /></QueuesProtectedRoute>} />

    {/* ── System Logs ─────────────────────────────────────────────────────── */}
    <Route path="/reports/system/audit-logs" element={<AuditLogsProtectedRoute><AuditLogsPage /></AuditLogsProtectedRoute>} />
    <Route path="/reports/system/user-activity" element={<UserActivityProtectedRoute><UserActivityPage /></UserActivityProtectedRoute>} />
    <Route path="/reports/system/change-history" element={<ChangeHistoryProtectedRoute><ChangeHistoryPage /></ChangeHistoryProtectedRoute>} />
    <Route path="/reports/system/security" element={<SecurityLogsProtectedRoute><SecurityLogsPage /></SecurityLogsProtectedRoute>} />
  </>
);
